// src/rms/utils/schemaEvaluator.js
/**
 * Schema visibility + validation DSL interpreter (client-side).
 * What:
 *   - Visibility predicates (visibleWhen) and input validators defined in schema JSON.
 *   - Lightweight mirror of the server validator so UX aligns with server authority.
 *
 * Visibility DSL (each item is an object):
 *   - Logical combinators:
 *     { any: [predicate, ...] }, { all: [predicate, ...] }, { not: predicate }
 *   - Atomic predicates:
 *     { questionId: 'referral_for', equals: 'myself' }
 *     { questionId: 'referral_for', oneOf: ['myself','someone_else'] }
 *     { questionId: 'ref_email', exists: true }  // non-empty
 *   - Context predicates (for post-account variants):
 *     { contextEquals: { path: 'pathway', value: 'cyp' } }
 *     { contextIn: { path: 'currentUser.role', values: ['school_user', 'parent'] } }
 *
 * Validators on questions:
 *   - pattern: { type: 'pattern', pattern: 'regex', flags?: 'i' } or { type: 'pattern', ref: 'email' }
 *   - sameAs:  { type: 'sameAs', otherQuestionId: 'ref_email' }
 *   - minLength / maxLength: { type: 'minLength', value: 3 }
 *
 * Notes:
 *   - Client validation is for UX only; server remains authoritative.
 *   - Keep this file in sync with server rules to avoid surprising users.
 */

export function getAnswerValue(answers, questionId) {
  if (!answers) return undefined;
  return answers[questionId];
}

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

/**
 * Accept the nested visibleWhen dialect used by some Firestore schemas —
 * `{equals: {questionId, value}}`, `{equals: {questionId, optionId}}`,
 * `{oneOf: {questionId, values}}` — normalised to the flat shape
 * (`{questionId, equals}` / `{questionId, oneOf}`). Without this, the nested
 * dialect compared an unfetchable answer against an object (always false), so
 * schema-configured follow-ups (theirLifestyle caffeine amount/impact,
 * theirEarlyLife periods) never appeared for guardians. Mirrors
 * _normalize_visibility_predicate in rms_validation.py — keep in sync.
 */
function normalizePredicate(predicate) {
  if (!predicate || typeof predicate !== 'object') return predicate;
  const eq = predicate.equals;
  if (eq && typeof eq === 'object' && !Array.isArray(eq) && eq.questionId !== undefined) {
    return {
      ...predicate,
      questionId: eq.questionId,
      equals: eq.value !== undefined ? eq.value : eq.optionId,
    };
  }
  const oo = predicate.oneOf;
  if (oo && typeof oo === 'object' && !Array.isArray(oo) && oo.questionId !== undefined) {
    return { ...predicate, questionId: oo.questionId, oneOf: oo.values || [] };
  }
  return predicate;
}

function evalPredicate(answers, predicate, context) {
  if (!predicate) return true;
  predicate = normalizePredicate(predicate);
  // Supported shapes: {equals, oneOf, exists} with questionId; logical {any, all, not}
  if (predicate.any) return predicate.any.some(p => evalPredicate(answers, p, context));
  if (predicate.all) return predicate.all.every(p => evalPredicate(answers, p, context));
  if (predicate.not) return !evalPredicate(answers, predicate.not, context);

  // Context predicates
  if (predicate.contextEquals) {
    const { path, value } = predicate.contextEquals;
    return getByPath(context || {}, path) === value;
  }
  if (predicate.contextIn) {
    const { path, values } = predicate.contextIn;
    const v = getByPath(context || {}, path);
    return Array.isArray(values) ? values.includes(v) : false;
  }

  const { questionId, equals, oneOf, exists } = predicate;
  const v = getAnswerValue(answers, questionId);
  if (typeof exists === 'boolean') return exists ? v !== undefined && v !== '' : v === undefined || v === '';
  if (oneOf) return oneOf.includes(v?.optionId ?? v);
  if (equals !== undefined) return (v?.optionId ?? v) === equals;
  return true;
}

export function isQuestionVisible(answers, visibleWhen, context) {
  /**
   * Evaluate a list of visibility rules against current answers.
   * Returns true when no rules are provided.
   */
  if (!visibleWhen) return true;
  // Allow either an array of rules or a single predicate object
  if (Array.isArray(visibleWhen)) {
    if (visibleWhen.length === 0) return true;
    return visibleWhen.every(rule => evalPredicate(answers, rule, context));
  }
  // Single predicate object
  return evalPredicate(answers, visibleWhen, context);
}

/**
 * Whether a schema SECTION would have been visible to a given respondent when
 * they filled the form. The dual-respondent referral schemas
 * (requestForReferral, reasonForReferral, educationHistory) gate their
 * sections with `visibleWhen` context rules on `currentUser.role`
 * ('school_user' | 'guardian') and `initiatedBy` ('school' | 'guardian').
 *
 * Clinician-facing review surfaces must apply this before rendering a
 * respondent's submission, so the OTHER respondent's sections are not shown
 * as unanswered questions. Sections without gating are visible to everyone.
 *
 * @param {object} section - schema section node (may carry `visibleWhen`)
 * @param {string} role - respondent whose submission is shown: 'school_user' | 'guardian'
 * @param {string} [initiatedBy] - who initiated the referral (patientRecord.referrerType); defaults to 'school'
 */
export function isSectionVisibleForRole(section, role, initiatedBy = 'school') {
  const visibleWhen = section?.visibleWhen;
  if (!visibleWhen) return true;
  const context = {
    pathway: 'cyp',
    initiatedBy: initiatedBy || 'school',
    currentUser: { role },
  };
  // Sections gate on context only, so answers are irrelevant here.
  return isQuestionVisible({}, visibleWhen, context);
}

/**
 * Evaluate an HFP-style conditional gate on a question.
 *
 * HFP v2 "modifier" questions (e.g. the Sensory Seeking/Avoidance checkbox items)
 * carry `meta.hfp.conditionalOn = { questionId, value }` and must only appear once
 * the parent gate question holds the trigger value (e.g. b1301 === 'ao_1', "Much
 * more"). This mirrors HfpV2DemoPage and the scoring contract. Returns true when
 * there is no conditional gate.
 *
 * @param {Record<string, any>} answers - flat questionId -> value map for the form
 * @param {object} q - a schema question node
 * @returns {boolean} true when the gate is satisfied or absent
 */
export function isConditionalMet(answers, q) {
  const cond = q?.meta?.hfp?.conditionalOn;
  if (!cond || !cond.questionId) return true;
  return answers?.[cond.questionId] === cond.value;
}

// -------- Variants and optionSets resolution (DSL v2) --------

function deepMerge(target, source) {
  if (!source) return target;
  const out = Array.isArray(target) ? [...target] : { ...target };
  Object.keys(source).forEach((key) => {
    const sv = source[key];
    const tv = out[key];
    if (sv && typeof sv === 'object' && !Array.isArray(sv) && tv && typeof tv === 'object' && !Array.isArray(tv)) {
      out[key] = deepMerge(tv, sv);
    } else {
      out[key] = sv;
    }
  });
  return out;
}

function resolveVariants(node, answers, context) {
  /**
   * Apply the first matching variant's overrides onto a shallow clone of node.
   * Variants shape: [{ when: predicate, overrides: { ...partialNode } }, ...]
   */
  if (!node || !Array.isArray(node.variants) || node.variants.length === 0) {
    return { ...node };
  }
  const base = { ...node };
  for (const variant of node.variants) {
    if (variant?.when && evalPredicate(answers, variant.when, context)) {
      return deepMerge(base, variant.overrides || {});
    }
  }
  return base;
}

function resolveOptionsRef(schema, node, answers, context) {
  /**
   * Resolve node.options from schema.optionSets using optionsRef.
   * 
   * optionsRef can be:
   *   1. A string: Direct reference to an optionSet name (e.g., "relationship_cyp")
   *   2. An object: Conditional resolution with shape:
   *      { when: [{ if: predicate, use: 'optionSetName' }, ...], default?: 'optionSetName' }
   * 
   * This allows schemas to use either simple direct references or conditional
   * option sets based on form answers.
   */
  if (!node) return node;
  const out = { ...node };
  const optRef = node.optionsRef;
  if (!optRef) return out;
  const optionSets = schema?.optionSets || {};
  let selectedSet = null;
  
  // Handle string optionsRef (direct reference to option set name)
  if (typeof optRef === 'string') {
    selectedSet = optRef;
  } else {
    // Handle object optionsRef (conditional resolution)
    const clauses = Array.isArray(optRef.when) ? optRef.when : [];
    for (const clause of clauses) {
      if (clause?.if && evalPredicate(answers, clause.if, context)) {
        selectedSet = clause.use;
        break;
      }
    }
    if (!selectedSet && optRef.default) {
      selectedSet = optRef.default;
    }
  }
  
  if (selectedSet && Array.isArray(optionSets[selectedSet])) {
    out.options = optionSets[selectedSet];
  }
  return out;
}

export function resolveQuestionNode(schema, question, answers, context) {
  /**
   * Resolve a question node with variants and optionsRef applied.
   * Returns a new object; does not mutate the input.
   */
  if (!question) return question;
  // Apply variants first so optionsRef can be overridden by variants if needed
  const withVariant = resolveVariants(question, answers, context);
  // Then resolve optionsRef into concrete options
  const withOptions = resolveOptionsRef(schema, withVariant, answers, context);
  // Children of group/repeatable_group can also have variants; resolve shallowly here for renderers that expect resolved children
  if (Array.isArray(withOptions.children)) {
    withOptions.children = withOptions.children.map((ch) => resolveQuestionNode(schema, ch, answers, context));
  }
  return withOptions;
}

export function resolveSectionNode(schema, section, answers, context) {
  /**
   * Resolve a section node (title/help via variants) and its questions.
   */
  if (!section) return section;
  const resolved = resolveVariants(section, answers, context);
  resolved.questions = (resolved.questions || []).map((q) => resolveQuestionNode(schema, q, answers, context));
  return resolved;
}

export function validateSameAs(answers, questionId, otherQuestionId) {
  /**
   * Compare two answer values (stringified) for equality.
   * Treats null/undefined as pass (defer to required rule).
   */
  const a = getAnswerValue(answers, questionId);
  const b = getAnswerValue(answers, otherQuestionId);
  if (a == null || b == null) return true;
  return String(a).trim() === String(b).trim();
}

export function validatePattern(value, regex) {
  /**
   * Validate a single value via provided regex spec { pattern, flags }.
   * Returns true on invalid regex (do not hard fail UX).
   */
  if (!regex) return true;
  try {
    const re = new RegExp(regex.pattern, regex.flags || '');
    return re.test(String(value || ''));
  } catch (e) {
    return true;
  }
}

// -------- NHS number (TDI-510) --------

// The example is a 999-range TEST number that passes the modulus 11 check
// (TDI-788): people copy the example into test environments, so it must never
// be a number that could belong to a real person. Keep it in step with
// rms_validation.py (NHS_NUMBER_MESSAGE) and the staging test answers.
export const NHS_NUMBER_MESSAGE = 'Enter their NHS number as 10 digits, for example 999 000 0018';
// Ten digits that are not a real NHS number (the check digit fails, or every
// digit is the same): say so, and point to the way on.
export const NHS_NUMBER_INVALID_MESSAGE = 'This is not a valid NHS number. Check the number, or choose "Don’t know / Not sure".';

export function isValidNhsNumber(text) {
  /**
   * A real NHS number: 10 digits whose last digit is the modulus 11 check
   * digit (NHS Data Dictionary). Spaces are allowed anywhere, nothing else
   * is, so a sentence typed into the box fails here rather than travelling
   * to the service and into the EMIS handover.
   * Mirrors is_valid_nhs_number in rms_validation.py; keep the two in step.
   */
  const raw = String(text == null ? '' : text);
  if (!/^[\d ]+$/.test(raw)) return false;
  const digits = raw.replace(/ /g, '');
  if (!/^\d{10}$/.test(digits)) return false;
  // One digit ten times passes the check digit but is never issued.
  if (/^(\d)\1{9}$/.test(digits)) return false;
  const total = digits.slice(0, 9).split('').reduce((acc, d, i) => acc + Number(d) * (10 - i), 0);
  let check = 11 - (total % 11);
  if (check === 11) check = 0;
  if (check === 10) return false;
  return check === Number(digits[9]);
}

// -------- Schema-driven validation --------

export function buildValidatorIndex(schema) {
  /**
   * Index named validators at schema.validators for { ref: 'name' } lookups.
   * @param {object} schema
   * @returns {Record<string, {pattern?: string, flags?: string}>}
   */
  const idx = {};
  (schema?.validators || []).forEach(v => {
    if (v?.name) idx[v.name] = v;
  });
  return idx;
}

function stringValueFromAnswer(v) {
  if (v == null) return '';
  if (typeof v === 'object') {
    if (v.otherText) return String(v.otherText || '');
    if (v.optionId) return String(v.optionId || '');
  }
  return String(v);
}

export function applyValidator(valIdx, vSpec, answers, questionId) {
  /**
   * Apply one validator vSpec to the current question's answer.
   * Supports pattern (inline or ref), sameAs, minLength, maxLength.
   * Returns true when validator passes or is unknown.
   */
  const type = vSpec?.type;
  if (type === 'pattern') {
    const ref = vSpec.ref;
    const pat = ref && valIdx[ref] ? valIdx[ref] : vSpec;
    const pattern = pat?.pattern;
    const flags = pat?.flags || '';
    if (!pattern) return true;
    try {
      const re = new RegExp(pattern, flags);
      return re.test(stringValueFromAnswer(getAnswerValue(answers, questionId)));
    } catch (e) {
      return true;
    }
  }
  if (type === 'nhsNumber') {
    // Scoped to one option of a radio when the spec names it ("Yes, I know
    // it"): the other options carry no number to check.
    const v = getAnswerValue(answers, questionId);
    const selected = (v && typeof v === 'object') ? v.optionId : v;
    if (vSpec.optionId && selected !== vSpec.optionId) return true;
    const text = (v && typeof v === 'object') ? v.otherText : v;
    // An empty box is the required rule's finding, not this one's.
    if (!String(text == null ? '' : text).trim()) return true;
    return isValidNhsNumber(text);
  }
  if (type === 'sameAs') {
    const other = vSpec.otherQuestionId;
    return stringValueFromAnswer(getAnswerValue(answers, questionId)) === stringValueFromAnswer(getAnswerValue(answers, other));
  }
  if (type === 'minLength') {
    const n = Number(vSpec.value || 0);
    return stringValueFromAnswer(getAnswerValue(answers, questionId)).length >= n;
  }
  if (type === 'maxLength') {
    const n = Number(vSpec.value || 999999);
    return stringValueFromAnswer(getAnswerValue(answers, questionId)).length <= n;
  }
  return true;
}

export function freeTextValidationMessage(question, value, valIdx) {
  /**
   * The line to show under a radio option's free-text box while what is
   * typed there fails a validator scoped to that option (TDI-510). Null
   * while the box is empty (the "add an answer" line covers that) and when
   * nothing is wrong, so the message appears only once there is something
   * to correct.
   */
  if (!question || question.type !== 'radio') return null;
  const selected = (value && typeof value === 'object') ? value.optionId : value;
  const text = (value && typeof value === 'object') ? String(value.otherText || '') : '';
  if (!selected || !text.trim()) return null;
  for (const vs of question.validators || []) {
    if (!vs || vs.optionId !== selected) continue;
    if (!applyValidator(valIdx || {}, vs, { [question.questionId]: value }, question.questionId)) {
      if (vs.message) return vs.message;
      if (vs.type === 'nhsNumber') {
        return /^\d{10}$/.test(text.replace(/ /g, '')) ? NHS_NUMBER_INVALID_MESSAGE : NHS_NUMBER_MESSAGE;
      }
      return 'Check this answer and try again';
    }
  }
  return null;
}

export function validateBySchema({ schema, question, answers, valIdx }) {
  /**
   * Validate a single question against required rules and its validators array.
   * Group questions are validated at child level; address has structured requirements.
   * @returns {boolean}
   */
  const qtype = question?.type;
  const qid = question?.questionId;
  if (!qid) return true;
  const v = getAnswerValue(answers, qid);

  // Required base checks
  if (question.required) {
    if (qtype === 'group') {
      // handled at child level
    } else if (qtype === 'address') {
      const ok = !!(v?.addr_street) && !!(v?.addr_city) && !!(v?.addr_postcode);
      if (!ok) return false;
    } else if (qtype === 'checkboxes') {
      // Expect an array of selections: [{ optionId, otherText? }, ...]
      const arr = Array.isArray(v) ? v : [];
      if (arr.length === 0) return false;
    } else if (qtype === 'radio') {
      const sel = v?.optionId ?? v;
      if (!sel) return false;
    } else {
      if (String(v ?? '').trim() === '') return false;
    }
  }

  // Apply validators array if present
  const validators = question.validators || [];
  for (const vs of validators) {
    if (!applyValidator(valIdx || {}, vs, answers, qid)) {
      return false;
    }
  }
  // Free-text rule for radios, applied whether or not the question itself is
  // required: selecting an option with allowsFreeText requires otherText
  // UNLESS that option has freeTextOptional: true. This matches the server
  // (rms_validation.py, 'other_required') and the checkbox rule below. When it
  // only ran inside the required block, an OPTIONAL radio with an empty
  // free-text box sailed past every step and then failed server validation at
  // submit with the generic banner — the SENDCo educationHistory dead end
  // (reports_concerns / differences_home_school / strategies_made_worse are
  // the only such questions across the referral forms).
  if (qtype === 'radio') {
    const sel = v?.optionId ?? v;
    if (sel) {
      const selectedOpt = (question.options || []).find(o => o.optionId === sel);
      if (selectedOpt?.allowsFreeText && selectedOpt?.freeTextOptional !== true) {
        if (!v?.otherText || String(v.otherText).trim() === '') return false;
      }
    }
  }
  // Additional rule for checkboxes: for ANY selected option with allowsFreeText (not just 'other'),
  // require otherText UNLESS that option has freeTextOptional: true.
  // This ensures consistent behavior where free text fields are required by default when triggered
  // by selecting an option, but can be made optional via schema configuration.
  if (qtype === 'checkboxes') {
    const arr = Array.isArray(v) ? v : [];
    const optionsWithFreeText = (question.options || []).filter(o => o.allowsFreeText);
    for (const opt of optionsWithFreeText) {
      // Find if this option is selected in the answers array
      const selectedItem = arr.find(x => (x?.optionId ?? x) === opt.optionId);
      if (selectedItem) {
        // Free text is required unless freeTextOptional is explicitly true on this option
        const freeTextRequired = opt.freeTextOptional !== true;
        if (freeTextRequired) {
          const hasValidFreeText = selectedItem.otherText && String(selectedItem.otherText).trim() !== '';
          if (!hasValidFreeText) {
            return false;
          }
        }
      }
    }
  }
  return true;
}


